import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";

import * as actions from "../store/actions/actions";
import {
  Box,
  Chip,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@material-ui/core";

import SearchReceiver from "header/SearchReceiver";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import {
  useRole,
  useFeatureAbility,
  useResourceAbility,
} from "libs/security/authorization/Permission";
import { Feature } from "libs/security/authorization/FeatureAbility";
import CertificateIcon from "./icons/CertificateIcon";
import SectorIcon from "./icons/SectorIcon";
import ProductIcon from "./icons/productIcon";
import CodeRoundedIcon from "@material-ui/icons/CodeRounded";
import PeopleOutlineRoundedIcon from "@material-ui/icons/PeopleOutlineRounded";
import AssignmentIcon from "@material-ui/icons/Assignment";
import TeamSelectionForm from "pages/home/team/forms/TeamSelectionForm";
import { NavigationPanel } from "./navigation-menu/NavigationPanel";
import { NavigationSkeleton } from "./NavigationSkeleton";
import { NewAuthRolePermissions } from "modus/components/authorizer/Authorizer";

interface VerticalNavigationProps {
  isShow: boolean;
  isShowFullSize: boolean;
}
const VerticalNavigation: React.FC<VerticalNavigationProps> = ({
  isShow,
  isShowFullSize,
}) => {
  const globalUser = useSelector((globalState: any) => globalState.userManager);
  const [isMainMenuOpen, toggleMainMenuOpen] = useState<any>({});
  const [rolepermission, state] = useRole();
  const [user, setUser] = useState<any>();
  const [photo, setPhoto] = useState<string>();
  const dispatch = useDispatch();

  const hasFeature = useFeatureAbility({
    id: Feature.ApiVerification,
  });

  const [,] = useResourceAbility({
    resource: ({ Certificate }) => Certificate.list.environment["trimble-prod"],
  });
  const verticalMenuDetails: any = {};

  verticalMenuDetails["my_teams"] = {
    name: "Teams",
    icon: <SectorIcon style={{ color: "#fff" }} />,
    url: `/home/teams/${rolepermission?.sector}/list`,
  };
  if(process.env.REACT_APP_USER_ONBOARDING_FLOW !== "true" ) {
    verticalMenuDetails["individual_users"] = {
      name: "My Team",
      icon: <PeopleOutlineRoundedIcon style={{ color: "#fff" }} />,
      url: `/home/teams/${rolepermission?.team}/users`,
    };
  }
  if(process.env.REACT_APP_USER_ONBOARDING_FLOW !== "true" ) {
    verticalMenuDetails["user_management"] = {
      name: "User Management",
      icon: <PeopleOutlineRoundedIcon style={{ color: "#fff" }} />,
    };
  }


  useEffect(() => {
    if ((state.loaded || state.intermediate) && !globalUser.role) {
      dispatch({
        type: actions.storeUserRole,
        payload: rolepermission,
      });
    }

  }, [
    globalUser.role,
    dispatch,
    rolepermission,
    state.loaded,
    state.intermediate,
  ]);

  const toggleMainMenu = useCallback(
    (index: string) => {
      let toggler = { ...isMainMenuOpen };
      toggler[index] = !toggler[index];
      toggleMainMenuOpen(toggler);
    },
    [isMainMenuOpen]
  );

  let navigationMenuList: Array<any> = [];

  let newAuthRolepermissions: NewAuthRolePermissions = useSelector((state: any) => state.userManager?.newAuthRolePermissions) ?? {};

  const generateNavigationMenu = () => {
    if (rolepermission?.menus?.length > 0) {
      rolepermission?.menus?.forEach((mainMenu: any) => {


        let subMenuList: Array<any> = [];
        mainMenu.menus.forEach((subMenu: any) => {
          if (verticalMenuDetails[subMenu.key]) {
            let subMenuItem = (
              <ListItem
                button
                className={
                  rolepermission?.approved === "INITIATED" &&
                  verticalMenuDetails[subMenu.key].isApprovalNeeded
                    ? "disabled-nav-link"
                    : ""
                }
              >
                <ListItemIcon>
                  {verticalMenuDetails[subMenu.key].icon}
                </ListItemIcon>
                <ListItemText
                  className="sub-menu-text"
                  primary={<Box display="flex"><Box flexGrow={1}>{verticalMenuDetails[subMenu.key].name}</Box>
                                <Box>{verticalMenuDetails[subMenu.key]?.deprecated && <Chip label="deprecated" size="small"/> }</Box>  </Box>}
                />
              </ListItem>
            );

            if (
              !verticalMenuDetails[subMenu.key].externalLink ||
              (rolepermission?.approved === "INITIATED" &&
                verticalMenuDetails[subMenu.key].isApprovalNeeded)
            ) {
              let navUrl = verticalMenuDetails[subMenu.key].url;

              if (
                rolepermission?.approved === "INITIATED" &&
                verticalMenuDetails[subMenu.key].isApprovalNeeded
              ) {
                navUrl = verticalMenuDetails[subMenu.key].waitingUrl;
              }
              subMenuList.push(
                <NavLink
                  className="navigation-tab"
                  key={subMenu.key}
                  activeClassName="active"
                  to={navUrl}
                >
                  {subMenuItem}
                </NavLink>
              );
            } else {
              subMenuList.push(
                <a
                  className="navigation-tab"
                  key={subMenu.key}
                  href={verticalMenuDetails[subMenu.key].url}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {subMenuItem}
                </a>
              );
            }
          }
        });
        let mainMenuItem = (
          <Collapse
            in={!isMainMenuOpen[mainMenu.key]}
            timeout="auto"
            unmountOnExit
          >
            <List component="div" disablePadding>
              {subMenuList}
            </List>
          </Collapse>
        );
        if (verticalMenuDetails[mainMenu.key]) {
          navigationMenuList.push(
            <List component="nav" key={mainMenu.key}>
              <ListItem
                button
                onClick={() => {
                  toggleMainMenu(mainMenu.key);
                }}
                className={                  
                  rolepermission?.approved === "INITIATED" &&
                  verticalMenuDetails[mainMenu.key].isApprovalNeeded
                    ? "disabled-nav-link nav-link-item"
                    : "nav-link-item"
                }
              >
                <ListItemText
                  className="main-menu-text"
                  primary={verticalMenuDetails[mainMenu.key].name}
                />
                {isMainMenuOpen[mainMenu.key] ? (
                  <ExpandLess className="main-menu-drop-icon" />
                ) : (
                  <ExpandMore className="main-menu-drop-icon" />
                )}
              </ListItem>
              {mainMenuItem}
            </List>
          );
        }
      });
    
    }
  };

  generateNavigationMenu();
  useEffect(() => {
    generateNavigationMenu();
  }, []);

  let group;
  if (state.loaded || state.intermediate) {
    group =
      globalUser.role === "SECTOR ADMIN" ? globalUser.sector : globalUser.teamDisplayName;
  }

  return (
    <>
      <div
        className={
          "vertical-navigation" +
          (isShow ? " nav-show" : "") +
          (!isShowFullSize ? " short-view" : " ")
        }
      >
        <div className="navigation-panel">
          {navigationMenuList?.length > 0 && (
            <>
              <NavigationPanel/>
              {navigationMenuList}
            </>)
          }
          {
            (!navigationMenuList || navigationMenuList.length === 0) &&  
            (  <NavigationPanel />
            )
          }
          </div>
      </div>
    </>
  );
};

export default VerticalNavigation;
